import React, { useState, useEffect } from "react";
import {
    Box,
    VStack,
    HStack,
    Text,
    Button,
    Input,
    FormControl,
    FormLabel,
    useColorModeValue,
    useToast,
    Alert,
    AlertIcon,
    AlertDescription,
    SimpleGrid,
    Heading,
    Icon,
    IconButton,
} from "@chakra-ui/react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import PageShell from "../layout/PageShell";
import { FiCreditCard, FiCheck, FiX, FiEdit2 } from "react-icons/fi";

const PAYMENT_CONFIG_COLLECTION = "paymentProcessorConfig";
const PAYMENT_CONFIG_DOC_ID = "global";

interface PaymentProcessorConfig {
    squareAppId?: string;
    cloverAppId?: string;
    cloverAppSecret?: string;
    cloverEnvironment?: string;
    chaseSolutionId?: string;
    globalPaymentsAcceptorId?: string;
    elavonVendorId?: string;
    elavonPartnerId?: string;
    monerisDeveloperId?: string;
    lastUpdated?: any;
    updatedBy?: string;
}

interface EditableFieldProps {
    label: string;
    field: keyof PaymentProcessorConfig;
    value: string;
    placeholder: string;
    onSave: (field: keyof PaymentProcessorConfig, value: string) => Promise<void>;
}

const EditableField: React.FC<EditableFieldProps> = ({
    label,
    field,
    value,
    placeholder,
    onSave
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value);
    const [saving, setSaving] = useState(false);

    const textColor = useColorModeValue("gray.700", "white");
    const textSecondary = useColorModeValue("gray.500", "gray.400");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const inputBg = useColorModeValue("gray.50", "gray.800");

    useEffect(() => {
        setTempValue(value);
    }, [value]);

    const handleLocalSave = async () => {
        setSaving(true);
        try {
            await onSave(field, tempValue);
            setIsEditing(false);
        } catch (e) {
            // Error handled in parent toast
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setTempValue(value);
        setIsEditing(false);
    };

    const maskValue = (val: string) => {
        if (!val) return "";
        return "•".repeat(Math.min(val.length, 20));
    };

    return (
        <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={textColor}>{label}</FormLabel>
            <HStack spacing={2}>
                <Box flex="1" position="relative">
                    {isEditing ? (
                        <Input
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            placeholder={placeholder}
                            bg={inputBg}
                            size="sm"
                            borderRadius="md"
                        />
                    ) : (
                        <Box
                            px={3}
                            py={2}
                            bg={inputBg}
                            borderRadius="md"
                            border="1px solid"
                            borderColor={borderColor}
                            minH="32px"
                            display="flex"
                            alignItems="center"
                        >
                            <Text fontSize="sm" color={value ? textColor : textSecondary}>
                                {value ? maskValue(value) : placeholder}
                            </Text>
                        </Box>
                    )}
                </Box>
                <HStack spacing={1}>
                    {isEditing ? (
                        <>
                            <IconButton
                                aria-label="Save"
                                icon={<Icon as={FiCheck as any} />}
                                size="sm"
                                colorScheme="green"
                                onClick={handleLocalSave}
                                isLoading={saving}
                            />
                            <IconButton
                                aria-label="Cancel"
                                icon={<Icon as={FiX as any} />}
                                size="sm"
                                variant="ghost"
                                onClick={handleCancel}
                            />
                        </>
                    ) : (
                        <IconButton
                            aria-label="Edit"
                            icon={<Icon as={FiEdit2 as any} />}
                            size="sm"
                            variant="outline"
                            borderColor={borderColor}
                            onClick={() => setIsEditing(true)}
                        />
                    )}
                </HStack>
            </HStack>
        </FormControl>
    );
};

const PaymentProcessorSettings: React.FC = () => {
    const toast = useToast();
    const { currentUser } = useAuth();
    const [config, setConfig] = useState<PaymentProcessorConfig>({
        squareAppId: "",
        cloverAppId: "",
        cloverAppSecret: "",
        cloverEnvironment: "",
        chaseSolutionId: "",
        globalPaymentsAcceptorId: "",
        elavonVendorId: "",
        elavonPartnerId: "",
        monerisDeveloperId: "",
    });

    const bgCard = useColorModeValue("white", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const textColor = useColorModeValue("gray.700", "white");
    const textSecondary = useColorModeValue("gray.500", "gray.400");

    useEffect(() => {
        const loadConfig = async () => {
            try {
                const configDocRef = doc(db, PAYMENT_CONFIG_COLLECTION, PAYMENT_CONFIG_DOC_ID);
                const configDoc = await getDoc(configDocRef);

                if (configDoc.exists()) {
                    const data = configDoc.data() as PaymentProcessorConfig;
                    setConfig({
                        squareAppId: data.squareAppId || "",
                        cloverAppId: data.cloverAppId || "",
                        cloverAppSecret: data.cloverAppSecret || "",
                        cloverEnvironment: data.cloverEnvironment || "",
                        chaseSolutionId: data.chaseSolutionId || "",
                        globalPaymentsAcceptorId: data.globalPaymentsAcceptorId || "",
                        elavonVendorId: data.elavonVendorId || "",
                        elavonPartnerId: data.elavonPartnerId || "",
                        monerisDeveloperId: data.monerisDeveloperId || "",
                    });
                }
            } catch (error) {
                console.error("Error loading Payment Processor config:", error);
            }
        };

        loadConfig();
    }, []);

    const handleUpdateField = async (field: keyof PaymentProcessorConfig, value: string) => {
        try {
            const configDocRef = doc(db, PAYMENT_CONFIG_COLLECTION, PAYMENT_CONFIG_DOC_ID);
            const updateData = {
                [field]: value,
                lastUpdated: serverTimestamp(),
                updatedBy: currentUser?.email || "unknown",
            };

            await setDoc(configDocRef, updateData, { merge: true });

            setConfig(prev => ({ ...prev, [field]: value }));

            toast({
                title: "Field Updated",
                description: `${field} saved successfully`,
                status: "success",
                duration: 2000,
                isClosable: true,
            });
        } catch (error: any) {
            console.error("Error saving field:", error);
            toast({
                title: "Error",
                description: error.message || "Failed to save field",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            throw error;
        }
    };

    return (
        <PageShell
            title="Payment Processor Settings"
            description="Platform-level IDs for payment integrations. Fields are masked for security."
            wide
        >
            <VStack spacing={6} align="stretch">

                <Alert status="info" borderRadius="xl">
                    <AlertIcon />
                    <AlertDescription>
                        These settings represent the platform's relationship with the payment processors.
                        Individual merchant credentials (like API Tokens) are configured within each store's settings.
                    </AlertDescription>
                </Alert>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {/* Square Section */}
                    <Box bg={bgCard} borderRadius="20px" p={6} border="1px" borderColor={borderColor} boxShadow="sm">
                        <VStack align="start" spacing={4} w="full">
                            <HStack spacing={3}>
                                <Icon as={FiCreditCard as any} boxSize={5} color="blue.500" />
                                <Text fontWeight="bold" fontSize="lg">Square</Text>
                            </HStack>
                            <EditableField
                                label="Application ID"
                                field="squareAppId"
                                value={config.squareAppId || ""}
                                placeholder="sq0idp-..."
                                onSave={handleUpdateField}
                            />
                        </VStack>
                    </Box>

                    {/* Clover Section */}
                    <Box bg={bgCard} borderRadius="20px" p={6} border="1px" borderColor={borderColor} boxShadow="sm">
                        <VStack align="start" spacing={4} w="full">
                            <HStack spacing={3}>
                                <Icon as={FiCreditCard as any} boxSize={5} color="green.500" />
                                <Text fontWeight="bold" fontSize="lg">Clover</Text>
                            </HStack>
                            <VStack spacing={4} w="full">
                                <EditableField
                                    label="App ID"
                                    field="cloverAppId"
                                    value={config.cloverAppId || ""}
                                    placeholder="Enter Clover App ID"
                                    onSave={handleUpdateField}
                                />
                                <EditableField
                                    label="App Secret"
                                    field="cloverAppSecret"
                                    value={config.cloverAppSecret || ""}
                                    placeholder="Enter Clover App Secret"
                                    onSave={handleUpdateField}
                                />
                                <EditableField
                                    label="Environment (Sandbox/Prod)"
                                    field="cloverEnvironment"
                                    value={config.cloverEnvironment || ""}
                                    placeholder="e.g. https://sandbox.dev.clover.com"
                                    onSave={handleUpdateField}
                                />
                            </VStack>
                        </VStack>
                    </Box>

                    {/* Moneris Section */}
                    <Box bg={bgCard} borderRadius="20px" p={6} border="1px" borderColor={borderColor} boxShadow="sm">
                        <VStack align="start" spacing={4} w="full">
                            <HStack spacing={3}>
                                <Icon as={FiCreditCard as any} boxSize={5} color="blue.400" />
                                <Text fontWeight="bold" fontSize="lg">Moneris</Text>
                            </HStack>
                            <EditableField
                                label="Developer ID"
                                field="monerisDeveloperId"
                                value={config.monerisDeveloperId || ""}
                                placeholder="Enter Moneris Developer ID"
                                onSave={handleUpdateField}
                            />
                        </VStack>
                    </Box>

                    {/* Chase Section */}
                    <Box bg={bgCard} borderRadius="20px" p={6} border="1px" borderColor={borderColor} boxShadow="sm">
                        <VStack align="start" spacing={4} w="full">
                            <HStack spacing={3}>
                                <Icon as={FiCreditCard as any} boxSize={5} color="blue.600" />
                                <Text fontWeight="bold" fontSize="lg">Chase (J.P. Morgan)</Text>
                            </HStack>
                            <EditableField
                                label="Solution ID"
                                field="chaseSolutionId"
                                value={config.chaseSolutionId || ""}
                                placeholder="Enter Solution ID"
                                onSave={handleUpdateField}
                            />
                        </VStack>
                    </Box>

                    {/* Global Payments Section */}
                    <Box bg={bgCard} borderRadius="20px" p={6} border="1px" borderColor={borderColor} boxShadow="sm">
                        <VStack align="start" spacing={4} w="full">
                            <HStack spacing={3}>
                                <Icon as={FiCreditCard as any} boxSize={5} color="gray.600" />
                                <Text fontWeight="bold" fontSize="lg">Global Payments</Text>
                            </HStack>
                            <EditableField
                                label="Acceptor ID"
                                field="globalPaymentsAcceptorId"
                                value={config.globalPaymentsAcceptorId || ""}
                                placeholder="Enter Acceptor ID"
                                onSave={handleUpdateField}
                            />
                        </VStack>
                    </Box>

                    {/* Elavon Section */}
                    <Box bg={bgCard} borderRadius="20px" p={6} border="1px" borderColor={borderColor} boxShadow="sm">
                        <VStack align="start" spacing={4} w="full">
                            <HStack spacing={3}>
                                <Icon as={FiCreditCard as any} boxSize={5} color="orange.500" />
                                <Text fontWeight="bold" fontSize="lg">Elavon (Converge)</Text>
                            </HStack>
                            <VStack spacing={4} w="full">
                                <EditableField
                                    label="Vendor ID"
                                    field="elavonVendorId"
                                    value={config.elavonVendorId || ""}
                                    placeholder="Enter Vendor ID"
                                    onSave={handleUpdateField}
                                />
                                <EditableField
                                    label="Partner ID"
                                    field="elavonPartnerId"
                                    value={config.elavonPartnerId || ""}
                                    placeholder="Enter Partner ID (Optional)"
                                    onSave={handleUpdateField}
                                />
                            </VStack>
                        </VStack>
                    </Box>
                </SimpleGrid>
            </VStack>
        </PageShell>
    );
};

export default PaymentProcessorSettings;
